import { useEffect, useRef } from 'react';
import maplibregl, { Map as MlMap } from 'maplibre-gl';
import { useSettings } from '@/store/settings';
import { loadJson, saveJson } from '@/lib/persist';
import { buildStyle } from './buildStyle';
import { registerPmtilesProtocol } from './protocol';

const CAMERA_KEY = 'fabricos.mapCamera.v1';

/** Hosts the Hub's /tilecache proxy mirrors (must match hub-core
 *  tile_proxy::ALLOWED_HOSTS). Requests to these are rewritten to the Hub so
 *  tiles come from its disk cache — LAN-fast and offline-capable. */
const CACHEABLE_HOSTS = new Set([
  'tiles.openfreemap.org',
  'protomaps.github.io',
  'api.maptiler.com',
]);

function toHubTileCache(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' || !CACHEABLE_HOSTS.has(u.hostname)) return null;
    return `${window.location.origin}/tilecache/${u.hostname}${u.pathname}${u.search}`;
  } catch {
    return null;
  }
}

/** Only rewrite once the Hub has proven it serves /tilecache — an older or
 *  unreachable Hub must degrade to direct upstream fetches, never a blank
 *  map. Vite's SPA fallback answers 200 text/html for unknown paths, hence
 *  the content-type check. */
let hubTileCacheOk = false;
let probeStarted = false;

function probeTileCache() {
  if (probeStarted) return;
  probeStarted = true;
  fetch('/tilecache/tiles.openfreemap.org/styles/liberty', { method: 'HEAD' })
    .then((r) => {
      const ct = r.headers.get('content-type') ?? '';
      hubTileCacheOk = r.ok && !ct.includes('text/html');
    })
    .catch(() => {
      hubTileCacheOk = false;
    });
}

interface PersistedCamera {
  center: [number, number] | null;
  zoom: number | null;
  pitch: number | null;
  bearing: number | null;
}

const NO_CAMERA: PersistedCamera = { center: null, zoom: null, pitch: null, bearing: null };

export interface MapBaseOptions {
  /** Called once after the first style finishes loading. */
  onReady?: (m: MlMap) => void;
  /** Called on every `load` + `styledata` so consumers can re-add their layers. */
  onStyleLoaded?: (m: MlMap) => void;
  /** Called immediately before the map is removed. */
  onDestroy?: (m: MlMap) => void;
  /** Add a metric scale control at bottom-left. Default: false. */
  showScale?: boolean;
  /** Override the initial view (otherwise pulled from settings). */
  initial?: { center?: [number, number]; zoom?: number; pitch?: number };
}

interface MapBaseHandle {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  mapRef: React.MutableRefObject<MlMap | null>;
}

/**
 * Shared MapLibre bootstrap. Three workspaces (Pilot, Mission, Replay) all
 * spin up a styled MapLibre map, install navigation controls, and re-style
 * (debounced + diffed) on settings change — this hook centralises that.
 *
 * Layer/source management stays with the consumer via `onStyleLoaded` because
 * each workspace renders a different overlay set (trails, waypoints, replay
 * route).
 */
export function useMapBase(opts: MapBaseOptions = {}): MapBaseHandle {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);
  const lastStyleKey = useRef<string>('');
  const styleDebounce = useRef<number | null>(null);

  const optsRef = useRef(opts);
  optsRef.current = opts;

  const settings = useSettings((s) => s.map);

  useEffect(() => {
    registerPmtilesProtocol();
    probeTileCache();
    const node = containerRef.current;
    if (!node) return;

    const initialStyle = buildStyle(settings);
    const savedCam = loadJson<PersistedCamera>(CAMERA_KEY, NO_CAMERA);
    const initialCenter =
      optsRef.current.initial?.center ??
      savedCam.center ??
      [settings.initialLon, settings.initialLat];
    const initialZoom =
      optsRef.current.initial?.zoom ?? savedCam.zoom ?? settings.initialZoom;
    const initialPitch =
      optsRef.current.initial?.pitch ?? savedCam.pitch ?? settings.initialPitch ?? 0;
    const m = new maplibregl.Map({
      container: node,
      style: initialStyle,
      center: initialCenter,
      zoom: initialZoom,
      pitch: initialPitch,
      bearing: savedCam.bearing ?? 0,
      attributionControl: { compact: true },
      fadeDuration: 150,
      // Tiles on disk are good enough — never re-download one because its
      // HTTP expiry passed. The hub cache serves day-fresh copies anyway.
      refreshExpiredTiles: false,
      // Read the toggle at request time (not hook time) so flipping the
      // setting applies to the very next tile without a map rebuild.
      transformRequest: (url) => {
        if (!hubTileCacheOk || useSettings.getState().map.cacheTiles === false) {
          return undefined;
        }
        const rewritten = toHubTileCache(url);
        return rewritten ? { url: rewritten } : undefined;
      },
    });
    // Persist camera pose across reload. Only listen to `moveend` — no need to
    // fire once per animation frame; MapLibre debounces this to the end of any
    // pan/zoom/pitch/rotate gesture.
    const persistCamera = () => {
      const c = m.getCenter();
      saveJson<PersistedCamera>(CAMERA_KEY, {
        center: [c.lng, c.lat],
        zoom: m.getZoom(),
        pitch: m.getPitch(),
        bearing: m.getBearing(),
      });
    };
    m.on('moveend', persistCamera);
    m.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    if (optsRef.current.showScale) {
      m.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    }
    mapRef.current = m;
    lastStyleKey.current = JSON.stringify(initialStyle);

    const handleStyleLoaded = () => optsRef.current.onStyleLoaded?.(m);
    m.on('styledata', handleStyleLoaded);
    m.on('load', () => {
      optsRef.current.onReady?.(m);
      handleStyleLoaded();
    });

    return () => {
      optsRef.current.onDestroy?.(m);
      m.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (styleDebounce.current !== null) window.clearTimeout(styleDebounce.current);
    styleDebounce.current = window.setTimeout(() => {
      const next = buildStyle(settings);
      const k = JSON.stringify(next);
      if (k === lastStyleKey.current) return;
      lastStyleKey.current = k;
      m.setStyle(next as maplibregl.StyleSpecification, { diff: true });
    }, 200);
    return () => {
      if (styleDebounce.current !== null) window.clearTimeout(styleDebounce.current);
    };
  }, [settings.source, settings.styleUrl, settings.apiKey, settings.pmtilesUrl]);

  return { containerRef, mapRef };
}
